import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ZoomIn, Move, RotateCw, Sun, Camera, Crosshair, RefreshCw,
  FlipHorizontal, Play, Ruler, ArrowRight, Type,
  Circle, Square, PenTool, Eye, Link2, Grid3X3, Grid2X2,
  ChevronDown, X, Spline, Cable, ChevronRight, ScanLine, Tags,
  Search, Layers, Box, PanelTop, Contrast, Maximize, Route,
  Orbit, RectangleHorizontal, BetweenHorizontalEnd, BetweenVerticalEnd,
  PanelLeft,PanelLeftClose,DraftingCompass,Activity,Ellipsis,Save,Star
} from 'lucide-react';

import './LeftToolbar.css';
import { utils } from '@ohif/core';
import { commandsManager, servicesManager } from '../App';

// Mở panel trái (danh sách chuỗi ảnh) — đường vào duy nhất trên điện thoại, nơi thanh thu gọn hai bên đã về 0 (xem
// thuGonKhongChiemCho trong ViewerLayout/constants/panels.ts).
// Lấy panel đầu tiên đang đăng ký chứ KHÔNG viết cứng id: panelService lưu theo entry.id (dạng
// rút gọn), không phải chuỗi '@ohif/extension-default.panelModule.seriesList' mà mode khai.
function moPanelTrai() {
  const panelService = servicesManager?.services?.panelService;
  const panel = panelService?.getPanels('left')?.[0];

  if (panel) {
    panelService.activatePanel(panel.id, true);
  }
}

const STORAGE_KEY = 'OHIF_LEFT_TOOLBAR_COLLAPSED';

const toggleTools = [
  'Zoom', 'Pan', 'WindowLevel', 'Crosshairs', 'TextMarker',
  'Length', 'Bidirectional', 'ArrowAnnotate', 'EllipticalROI',
  'RectangleROI', 'CircleROI', 'PlanarFreehandROI', 'SplineROI',
  'LivewireContour', 'StackScroll', 'Probe', 'Angle', 'CobbAngle',
  'CalibrationLine', 'Magnify', 'WindowLevelRegion'
];

const defaultCollapsed: Record<string, boolean> = {
  main: false,
  chieu: false,
  measure: true,
  common: true,
  layout: true,
};

const groups = [
  {
    id: 'main',
    title: 'Cơ bản',
    tools: [
      { icon: ZoomIn, label: 'Zoom', cmd: 'Zoom' },
      { icon: Move, label: 'Di chuyển', cmd: 'Pan' },
      { icon: Sun, label: 'Ánh sáng / Window Level', cmd: 'WindowLevel' },
      { icon: Camera, label: 'Chụp ảnh', cmd: 'Capture' },
      {
        icon: Star,
        label: 'Ảnh chính',
        moTa: 'Ảnh chính: đưa ảnh đang xem (kèm số đo, mũi tên) lên phiếu kết quả — bấm lần nữa để bỏ',
        cmd: 'AnhChinh',
      },
      { icon: Crosshair, label: 'Crosshair', cmd: 'Crosshairs' },
      { icon: Type, label: 'Text Marker', cmd: 'TextMarker' },
    ],
  },
  // Nút chữ, vẽ riêng bằng NhomChieuKhoi.
  { id: 'chieu', title: 'Chiếu khối (MIP)', tools: [] },
  {
    id: 'measure',
    title: 'Đo lường / Chú thích',
    tools: [
      { icon: Ruler, label: 'Đo chiều dài', cmd: 'Length' },
      { icon: X, label: 'Bidirectional', cmd: 'Bidirectional' },
      { icon: ArrowRight, label: 'Mũi tên ghi chú', cmd: 'ArrowAnnotate' },
      { icon: Circle, label: 'Ellipse ROI', cmd: 'EllipticalROI' },
      { icon: Square, label: 'Rectangle ROI', cmd: 'RectangleROI' },
      { icon: Circle, label: 'Circle ROI', cmd: 'CircleROI' },
      { icon: PenTool, label: 'Freehand ROI', cmd: 'PlanarFreehandROI' },
      { icon: Spline, label: 'Spline ROI', cmd: 'SplineROI' },
      { icon: Cable, label: 'Livewire Contour', cmd: 'LivewireContour' },
      { icon: DraftingCompass, label: 'Đo góc', cmd: 'Angle' },
      { icon: Activity , label: 'Cobb Angle', cmd: 'CobbAngle' },
    ],
  },
  {
    id: 'common',
    title: 'Tiện ích',
    tools: [
      { icon: RefreshCw, label: 'Reset', cmd: 'Reset' },
      { icon: RotateCw, label: 'Xoay phải', cmd: 'RotateRight' },
      { icon: FlipHorizontal, label: 'Lật ngang', cmd: 'FlipHorizontal' },
      { icon: Link2, label: 'Đồng bộ', cmd: 'Sync' },
      { icon: RectangleHorizontal, label: 'Reference Lines', cmd: 'ReferenceLines' },
      { icon: Layers, label: 'Overlay', cmd: 'Overlay' },
      { icon: Eye, label: 'Stack Scroll', cmd: 'StackScroll' },
      { icon: Contrast, label: 'Đảo màu', cmd: 'Invert' },
      { icon: Crosshair, label: 'Probe', cmd: 'Probe' },
      { icon: Play, label: 'Cine', cmd: 'Cine' },
      { icon: Ruler, label: 'Calibration Line', cmd: 'CalibrationLine' },
      { icon: Tags, label: 'DICOM Tag Browser', cmd: 'DicomTagBrowser' },
      { icon: Search, label: 'Kính lúp', cmd: 'Magnify' },
      { icon: Sun, label: 'Window Level Region', cmd: 'WindowLevelRegion' },
    ],
  },
  {
    id: 'layout',
    title: 'Bố cục',
    tools: [
      { icon: Square, label: 'Layout 1x1', cmd: 'Layout1x1' },
      { icon: BetweenHorizontalEnd, label: 'Layout 1x2', cmd: 'Layout1x2' },
      { icon: BetweenVerticalEnd, label: 'Layout 2x1', cmd: 'Layout2x1' },
      { icon: Grid2X2, label: 'Layout 2x2', cmd: 'Layout2x2' },
      { icon: Grid3X3, label: 'Layout 3x3', cmd: 'Layout3x3' },
      { icon: Box, label: 'MPR', cmd: 'MPR' },
      { icon: PanelTop, label: '3D 4 Up', cmd: 'Layout3D4Up' },
      { icon: Orbit, label: '3D Main', cmd: 'Layout3DMain' },
      { icon: PanelLeft, label: 'Axial Primary', cmd: 'primaryAxial' },
      { icon: Maximize, label: '3D Only', cmd: 'ThreeDOnly' },
      { icon: PanelLeftClose, label: 'Primary 3D', cmd: 'primary3D' },
      { icon: Layers, label: 'Frame', cmd: 'Frame' },
    ],
  },
];

const TAT_CA_NUT = groups.flatMap(group => group.tools);

// Thanh đáy điện thoại chỉ giữ việc hay làm nhất cho vừa một hàng (~390px); phần còn lại nằm trong tấm "Thêm".
const NUT_THANH_DUOI = ['WindowLevel', 'Zoom', 'Pan', 'StackScroll', 'Length', 'Reset'];

// Bố cục không đưa ra trên điện thoại: 3x3 quá nhỏ để đọc, dựng khối 3D thì GPU điện thoại dễ mất WebGL context (màn đen).
const BO_TREN_DIEN_THOAI = ['Layout3x3', 'Layout3D4Up', 'Layout3DMain', 'ThreeDOnly', 'primary3D'];

type ChieuKhoi = { cheDo: string; doDay: number | 'toanKhoi' | null } | null;

const CAC_CHE_DO = [
  { cheDo: 'thuong', nhan: 'Thường', moTa: 'Lát mỏng như thường' },
  { cheDo: 'mip', nhan: 'MIP', moTa: 'MIP — giữ điểm sáng nhất: mạch có thuốc, xương, nốt phổi' },
  { cheDo: 'minip', nhan: 'MinIP', moTa: 'MinIP — giữ điểm tối nhất: đường thở, khí' },
  { cheDo: 'avg', nhan: 'TB', moTa: 'Trung bình — như lát dày, ít nhiễu' },
];

const CAC_DO_DAY: { doDay: number | 'toanKhoi'; nhan: string }[] = [
  { doDay: 5, nhan: '5 mm' },
  { doDay: 10, nhan: '10 mm' },
  { doDay: 20, nhan: '20 mm' },
  { doDay: 'toanKhoi', nhan: 'Toàn khối' },
];

function NhomChieuKhoi({
  chieu,
  onChon,
}: {
  chieu: ChieuKhoi;
  onChon: (cheDo: string, doDay?: number | 'toanKhoi') => void;
}) {
  const cheDoDangDung = chieu?.cheDo;
  const dangChieu = !!cheDoDangDung && cheDoDangDung !== 'thuong';

  return (
    <div className="chieuKhoi">
      <div className="chieuHang">
        {CAC_CHE_DO.map(c => (
          <button
            key={c.cheDo}
            type="button"
            className={`chieuNut ${cheDoDangDung === c.cheDo ? 'active' : ''}`}
            title={c.moTa}
            aria-label={c.moTa}
            aria-pressed={cheDoDangDung === c.cheDo}
            onClick={() => onChon(c.cheDo, c.cheDo === 'thuong' ? undefined : chieu?.doDay ?? undefined)}
          >
            {c.nhan}
          </button>
        ))}
      </div>
      <div className="chieuHang">
        {CAC_DO_DAY.map(d => (
          <button
            key={String(d.doDay)}
            type="button"
            className={`chieuNut ${dangChieu && chieu?.doDay === d.doDay ? 'active' : ''}`}
            title={`Độ dày lớp chiếu: ${d.nhan}`}
            aria-label={`Độ dày lớp chiếu: ${d.nhan}`}
            aria-pressed={dangChieu && chieu?.doDay === d.doDay}
            onClick={() => onChon(dangChieu ? cheDoDangDung : 'mip', d.doDay)}
          >
            {d.nhan}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function LeftToolbar() {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const [activeCmd, setActiveCmd] = useState<string | null>(null);
  const [chieu, setChieu] = useState<ChieuKhoi>(null);

  // Điện thoại (utils.laManHinhHep — cả khi xoay ngang): panel trái là ngăn kéo phủ lên ảnh, để công cụ trong đó là mỗi
  // lần chọn phải mở ngăn kéo che ảnh ⇒ dựng thanh đáy thay cho thanh dọc, ở cả bản thường lẫn bản chia sẻ.
  const [manHinhHep, setManHinhHep] = useState(utils.laManHinhHep);
  useEffect(() => utils.theoDoiManHinhHep(setManHinhHep), []);
  const [moThem, setMoThem] = useState(false);

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : defaultCollapsed;
    } catch {
      return defaultCollapsed;
    }
  });

  // Thanh công cụ gắn ở cấp App (ngoài router) nên phải tự dò panel trái của trình xem. Dò liên tục chứ không chỉ một
  // lần: rời trình xem (về danh sách ca) thì panel mất — gỡ thanh, không thì thanh đáy nằm lại trên trang khác; đổi ca /
  // đổi chế độ thì panel dựng lại — gắn vào panel mới.
  useEffect(() => {
    let dangGan: HTMLElement | null = null;
    let ro: ResizeObserver | null = null;

    const timer = setInterval(() => {
      const el = document.getElementById('viewerLayoutResizableLeftPanel');
      if (el === dangGan) return;

      ro?.disconnect();
      ro = null;
      dangGan = el;
      setHost(el);
      if (el) {
        setIsNarrow(el.offsetWidth < 210);
        ro = new ResizeObserver(() => setIsNarrow(el.offsetWidth < 210));
        ro.observe(el);
      }
    }, 300);

    return () => {
      clearInterval(timer);
      ro?.disconnect();
    };
  }, []);

  // Chế độ chiếu của khung đang chọn — đọc lại mỗi khi đổi khung / đổi bố cục để nút sáng đúng khung.
  function capNhatChieu() {
    try {
      setChieu(commandsManager.runCommand('layCheDoChieu') ?? null);
    } catch {
      setChieu(null);
    }
  }

  useEffect(() => {
    const viewportGridService = servicesManager?.services?.viewportGridService;
    if (!host || !viewportGridService) return;

    const dangKy = [
      viewportGridService.EVENTS.ACTIVE_VIEWPORT_ID_CHANGED,
      viewportGridService.EVENTS.VIEWPORTS_READY,
      viewportGridService.EVENTS.LAYOUT_CHANGED,
    ].map(suKien => viewportGridService.subscribe(suKien, capNhatChieu));
    // Nhãn trên khung đổi (kể cả do Crosshairs kéo slab / khung dựng lại) — phát từ lệnh chiếu khối của cornerstone.
    window.addEventListener('ohif:chieu-khoi-doi', capNhatChieu);

    return () => {
      dangKy.forEach(d => d.unsubscribe());
      window.removeEventListener('ohif:chieu-khoi-doi', capNhatChieu);
    };
  }, [host]);

  // Công cụ mà chế độ OHIF đang chạy có đăng ký, ở BẤT KỲ nhóm công cụ nào của nó. Sang Phân vùng thì không còn Length,
  // Angle, các ROI… — nút của chúng bấm không ăn, nên ẩn. Xét cả chế độ chứ không theo khung đang chọn: theo khung thì
  // bấm sang khung 3D / MPR là cả nhóm nút biến mất rồi hiện lại, thanh công cụ nhảy liên tục. null = chưa có nhóm nào.
  const [congCuCo, setCongCuCo] = useState<Set<string> | null>(null);

  function capNhatCongCu() {
    const toolGroupService = servicesManager?.services?.toolGroupService;
    const cacNhom = (toolGroupService?.getToolGroupIds() ?? [])
      .map(id => toolGroupService.getToolGroup(id))
      .filter(Boolean);
    const moi = cacNhom.length ? new Set(toggleTools.filter(t => cacNhom.some(nhom => nhom.hasTool(t)))) : null;
    // VIEWPORT_ADDED bắn mỗi khung (4 lần khi 2x2): giữ nguyên Set cũ khi nội dung không đổi, khỏi render lại.
    setCongCuCo(cu => (cu && moi && cu.size === moi.size && [...moi].every(t => cu.has(t)) ? cu : moi));
  }

  useEffect(() => {
    const toolGroupService = servicesManager?.services?.toolGroupService;
    if (!host || !toolGroupService) return;

    // TOOLGROUP_CREATED phát lúc nhóm vừa tạo, CHƯA thêm công cụ (createToolGroupAndAddTools) — đọc ở lượt sau, khi
    // onModeEnter đã dựng xong mọi nhóm.
    const docLuotSau = () => setTimeout(capNhatCongCu, 0);
    const dangKy = [
      toolGroupService.subscribe(toolGroupService.EVENTS.TOOLGROUP_CREATED, docLuotSau),
      toolGroupService.subscribe(toolGroupService.EVENTS.VIEWPORT_ADDED, docLuotSau),
    ];
    capNhatCongCu();

    return () => dangKy.forEach(d => d.unsubscribe());
  }, [host]);

  // Nút đang bật mà bị ẩn (sang chế độ không có công cụ đó) thì bỏ trạng thái bật, không thì lúc hiện lại nó sáng như đang
  // bật mà bấm vào lại tắt đi.
  useEffect(() => {
    if (activeCmd && congCuCo && toggleTools.includes(activeCmd) && !congCuCo.has(activeCmd)) {
      setActiveCmd(null);
    }
  }, [congCuCo]);

  // Ảnh chính (RIS): nút chỉ có khi RIS phát giấy phép ghi cho người đang mở (bác sĩ có quyền Lập kết quả của ca — không
  // có ở PET-CT, trang chia sẻ); sáng khi ảnh đang xem của khung đang chọn đã là ảnh chính.
  const [coAnhChinh, setCoAnhChinh] = useState(false);
  const [laAnhChinh, setLaAnhChinh] = useState(false);

  useEffect(() => {
    const viewportGridService = servicesManager?.services?.viewportGridService;
    if (!host || !viewportGridService) return;

    let co = false;
    try {
      co = !!commandsManager.runCommand('coTheDanhDauAnhChinh');
    } catch {}
    setCoAnhChinh(co);
    if (!co) return;

    const capNhat = () => {
      try {
        setLaAnhChinh(!!commandsManager.runCommand('laAnhChinhDangXem'));
      } catch {
        setLaAnhChinh(false);
      }
    };
    const dangKy = [
      viewportGridService.EVENTS.ACTIVE_VIEWPORT_ID_CHANGED,
      viewportGridService.EVENTS.VIEWPORTS_READY,
    ].map(suKien => viewportGridService.subscribe(suKien, capNhat));
    // Ảnh mới của khung phát trên phần tử khung, không nổi bọt ⇒ nghe ở pha bắt.
    const SU_KIEN_ANH = ['CORNERSTONE_STACK_NEW_IMAGE', 'CORNERSTONE_VOLUME_NEW_IMAGE'];
    SU_KIEN_ANH.forEach(s => document.addEventListener(s, capNhat, true));
    window.addEventListener('ris-anh-chinh-doi', capNhat);
    capNhat();

    return () => {
      dangKy.forEach(d => d.unsubscribe());
      SU_KIEN_ANH.forEach(s => document.removeEventListener(s, capNhat, true));
      window.removeEventListener('ris-anh-chinh-doi', capNhat);
    };
  }, [host]);

  // Nút gọi setToolActive (tên nút = tên công cụ, liệt kê ở toggleTools) mà nhóm công cụ không có thì ẩn; nút lệnh
  // khác (Reset, bố cục, chụp ảnh…) chạy ở mọi chế độ nên giữ. Link ẩn danh bỏ bảng thẻ DICOM (bày họ tên / mã / ngày sinh).
  const nutHien = (cmd: string) =>
    !(cmd === 'DicomTagBrowser' && utils.laCheDoAnDanh()) &&
    (cmd !== 'AnhChinh' || coAnhChinh) &&
    (!congCuCo || !toggleTools.includes(cmd) || congCuCo.has(cmd));

  // Sau khi chờ (chuyển MPR có thể mất vài giây) người dùng có thể đã chọn khung khác ⇒ luôn đọc lại khung đang chọn.
  async function chonChieu(cheDo: string, doDay?: number | 'toanKhoi', tuReset = false) {
    try {
      await commandsManager.runCommand('datCheDoChieu', { cheDo, doDay, tuReset });
    } catch (e) {
      console.error('[ChieuKhoi]', e);
    }
    capNhatChieu();
  }

  function toggleGroup(id: string) {
    const next = {
      ...collapsed,
      [id]: !collapsed[id],
    };

    setCollapsed(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }

  function tryRun(names: string[], options?: any) {
    for (const name of names) {
      try {
        commandsManager.runCommand(name, options);
        return true;
      } catch {}
    }

    return false;
  }

  function cancelTool(cmd: string) {
    tryRun(['setToolPassive'], {
      toolName: cmd,
    });

    setActiveCmd(null);
  }

  function setLayout(row: number, col: number) {
    tryRun(['setViewportGridLayout'], {
      numRows: row,
      numCols: col,
    });
  }

  // setHangingProtocol trả false khi bố cục không đổi (vd máy vẽ bằng CPU không dựng được MPR / 3D — lệnh đã tự báo):
  // khi đó không sáng nút, kẻo nút báo một bố cục không có trên màn hình.
  function datBoCuc(protocolId: string, cmd?: string) {
    let daDoi = false;
    try {
      daDoi = commandsManager.runCommand('setHangingProtocol', { protocolId }) !== false;
    } catch {}
    if (daDoi && cmd) {
      setActiveCmd(cmd);
    }
  }

  function runTool(cmd: string) {
    if (toggleTools.includes(cmd) && activeCmd === cmd) {
      cancelTool(cmd);
      return;
    }

    switch (cmd) {
      case 'Reset':
        tryRun(['resetViewport', 'resetViewports']);
        chonChieu('thuong', undefined, true);
        setActiveCmd(null);
        break;

      case 'RotateRight':
        tryRun(['rotateViewportCW', 'rotateViewport', 'rotateRight'], {
          rotation: 90,
        });
        setActiveCmd(null);
        break;

      case 'FlipHorizontal':
        tryRun(['flipViewportHorizontal', 'flipHorizontal']);
        setActiveCmd(null);
        break;

      case 'Cine':
        tryRun(['toggleCine', 'cineStart', 'setCine']);
        setActiveCmd(activeCmd === 'Cine' ? null : 'Cine');
        break;

      case 'Invert':
        tryRun(['invertViewport', 'toggleInvert']);
        setActiveCmd(null);
        break;

      case 'Layout1x1':
        setLayout(1, 1);
        setActiveCmd(cmd);
        break;

      case 'Layout1x2':
        setLayout(1, 2);
        setActiveCmd(cmd);
        break;

      case 'Layout2x1':
        setLayout(2, 1);
        setActiveCmd(cmd);
        break;

      case 'Layout2x2':
        setLayout(2, 2);
        setActiveCmd(cmd);
        break;

      case 'Layout3x3':
        setLayout(3, 3);
        setActiveCmd(cmd);
        break;

      case 'DicomTagBrowser':
        tryRun(['showDicomTagBrowser', 'toggleDicomTagBrowser']);
        setActiveCmd(null);
        break;

      case 'Capture':
        tryRun(['showDownloadViewportModal', 'captureViewport', 'downloadViewportScreenshot']);
        setActiveCmd(null);
        break;

      // Không đụng activeCmd: đánh dấu xong bác sĩ dùng tiếp công cụ đang bật.
      case 'AnhChinh':
        tryRun(['danhDauAnhChinh']);
        break;
      case 'MPR':
        datBoCuc('mpr');
        break;

      case 'Layout3D4Up':
        datBoCuc('fourUp', cmd);
        break;

      case 'Layout3DMain':
        datBoCuc('main3D', cmd);
        break;

      case 'primaryAxial':
        datBoCuc('primaryAxial', cmd);
        break;

      case 'ThreeDOnly':
        datBoCuc('only3D', cmd);
        break;

      case 'primary3D':
        datBoCuc('primary3D', cmd);
        break;

      case 'Frame':
        datBoCuc('frame-view', cmd);
        break;
      default:
        tryRun(['setToolActive'], {
          toolName: cmd,
        });

        if (toggleTools.includes(cmd)) {
          setActiveCmd(cmd);
        } else {
          setActiveCmd(null);
        }

        break;
    }
  }

  if (!host || (isNarrow && !manHinhHep)) return null;

  function nutCongCu(tool: (typeof groups)[number]['tools'][number], key: string, coNhan = false) {
    const ToolIcon = tool.icon;
    const laNutAnhChinh = tool.cmd === 'AnhChinh';
    const sang = laNutAnhChinh ? laAnhChinh : activeCmd === tool.cmd;
    const moTa = laNutAnhChinh && laAnhChinh ? 'Ảnh đang xem đã là ảnh chính — bấm để bỏ khỏi phiếu' : tool.moTa ?? tool.label;
    return (
      <button
        key={key}
        className={`${coNhan ? 'themNut' : 'toolButton'} ${sang ? 'active' : ''}`}
        type="button"
        title={moTa}
        aria-label={moTa}
        aria-pressed={laNutAnhChinh ? laAnhChinh : undefined}
        onClick={() => {
          setMoThem(false);
          runTool(tool.cmd);
        }}
      >
        <ToolIcon size={17} strokeWidth={1.8} />
        {coNhan && <span>{tool.label}</span>}
      </button>
    );
  }

  if (manHinhHep) {
    const nutThanh = NUT_THANH_DUOI.map(cmd => TAT_CA_NUT.find(tool => tool.cmd === cmd))
      .filter(Boolean)
      .filter(tool => nutHien(tool.cmd));
    const nutTrongThem = (group: (typeof groups)[number]) =>
      group.tools.filter(tool => nutHien(tool.cmd) && !BO_TREN_DIEN_THOAI.includes(tool.cmd));
    const nhomThem = groups.filter(group => nutTrongThem(group).length > 0);

    return createPortal(
      // Thanh đáy nằm ngoài panel trái (panel là ngăn kéo, đóng thì rộng 0) nên gắn thẳng vào body.
      <div id="leftToolPanel" className="thanhDuoi">
        {moThem && (
          <>
            <div className="thanhDuoiNen" onClick={() => setMoThem(false)} />
            <div className="thanhDuoiThem" role="dialog" aria-label="Thêm công cụ">
              {nhomThem.map(group => (
                <div className="themNhom" key={group.id}>
                  <div className="themTieuDe">{group.title}</div>
                  <div className="themLuoi">
                    {nutTrongThem(group).map(tool => nutCongCu(tool, `them_${tool.cmd}`, true))}
                  </div>
                </div>
              ))}
              {/* Chiếu khối là việc của bác sĩ đọc CT/MR (bản chia sẻ cho người bệnh không cần). Chọn xong giữ tấm mở để
                  chỉnh tiếp độ dày. */}
              {!utils.laCheDoChiXem() && (
                <div className="themNhom">
                  <div className="themTieuDe">Chiếu khối (MIP)</div>
                  <NhomChieuKhoi chieu={chieu} onChon={chonChieu} />
                </div>
              )}
              {/* Lưu SR vốn ở thanh công cụ đầu trang, mà thanh đó ẩn trên điện thoại. Bản chia sẻ không ghi gì. */}
              {!utils.laCheDoChiXem() && (
                <div className="themNhom">
                  <div className="themTieuDe">Kết quả đo</div>
                  <div className="themLuoi">
                    <button
                      className="themNut"
                      type="button"
                      onClick={() => {
                        setMoThem(false);
                        tryRun(['saveSrToOrthanc']);
                      }}
                    >
                      <Save size={17} strokeWidth={1.8} />
                      <span>Lưu SR</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        <div className="toolGrid">
          <button
            className="toolButton"
            type="button"
            title="Chuỗi ảnh"
            aria-label="Chuỗi ảnh"
            onClick={() => {
              setMoThem(false);
              moPanelTrai();
            }}
          >
            <Layers size={17} strokeWidth={1.8} />
          </button>
          {nutThanh.map(tool => nutCongCu(tool, `thanh_${tool.cmd}`))}
          <button
            className={`toolButton ${moThem ? 'active' : ''}`}
            type="button"
            title="Thêm công cụ"
            aria-label="Thêm công cụ"
            aria-expanded={moThem}
            onClick={() => setMoThem(!moThem)}
          >
            <Ellipsis size={17} strokeWidth={1.8} />
          </button>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div id="leftToolPanel">
      {groups
        // Nhóm tự vẽ (tools rỗng, vd 'chieu') giữ nguyên; nhóm nút mà không còn nút nào dùng được thì bỏ cả tiêu đề.
        .filter(group => group.tools.length === 0 || group.tools.some(tool => nutHien(tool.cmd)))
        .map(group => {
        const close = collapsed[group.id];

        return (
          <div className={`toolGroup ${close ? 'collapsed' : ''}`} key={group.id}>
            <button
              className="toolTitleMini"
              type="button"
              title={group.title}
              onClick={() => toggleGroup(group.id)}
            >
              {close ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
              <span>{group.title}</span>
            </button>

            {!close && group.id === 'chieu' && <NhomChieuKhoi chieu={chieu} onChon={chonChieu} />}

            {!close && group.id !== 'chieu' && (
              <div className="toolGrid">
                {group.tools.filter(tool => nutHien(tool.cmd)).map(tool => nutCongCu(tool, `${group.id}_${tool.cmd}`))}
              </div>
            )}
          </div>
        );
      })}
    </div>,
    host
  );
}
